import React from 'react';
import { useParams, Link } from 'react-router-dom';
import PropertiesSection from './PropertiesSection';
import SEO from '../common/SEO';
import highlandsImage from '../../assets/neighborhoods/highlands.png';
import dawsonvilleImage from '../../assets/neighborhoods/dawsonville.png';
import castleberryMasterPlan from '../../assets/neighborhoods/castleberry_ridge.png';

// This would typically come from an API or database
const neighborhoodData = {
  highlands: {
    id: 'highlands',
    name: 'The Highlands',
    location: 'Rome, GA',
    description: 'Nestled in the rolling hills of Rome, this beautiful community offers stunning mountain views and easy access to downtown.',
    longDescription: 'The Highlands is our premier community in Rome, GA, offering a perfect blend of natural beauty and modern convenience. Residents enjoy spacious lots, stunning mountain views, and a welcoming community atmosphere. With easy access to downtown Rome, excellent schools, and abundant outdoor recreation, The Highlands provides an ideal setting for your new home.',
    image: highlandsImage,
    priceRange: 'From the $400s',
    amenities: ['Community Clubhouse', 'Swimming Pool', 'Walking Trails', 'Playground', 'Picnic Area'],
    homes: ['Aurora', 'Victoria', 'Diana', 'Ceres']
  },
  castleberry: {
    id: 'castleberry',
    name: 'Castleberry Ridge',
    location: 'Dawsonville, GA',
    description: 'Located in charming Dawsonville, this family-friendly neighborhood features spacious lots and excellent schools.',
    longDescription: 'Castleberry Ridge is a charming, family-friendly community in Dawsonville, GA. With its spacious homesites, mature trees, and peaceful setting, it\'s the perfect place to build your dream home. The community offers easy access to shopping, dining, and recreation, as well as top-rated schools. Enjoy the best of small-town living with all the conveniences of modern life.',
    image: dawsonvilleImage,
    priceRange: 'From the $450s',
    amenities: ['Community Park', 'Nature Trails', 'Tennis Courts', 'Pavilion'],
    homes: ['Pomona', 'Terra', 'Vesta', 'Cybele']
  }
};

const Neighborhood: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const neighborhood = id ? neighborhoodData[id as keyof typeof neighborhoodData] : undefined;

  if (!neighborhood) {
    return (
      <div className="container-custom py-12 md:py-24 text-center">
        <h1 className="mb-8">Neighborhood Not Found</h1>
        <p className="text-lg text-gray-700 mb-8">
          The neighborhood you are looking for could not be found.
        </p>
        <Link to="/neighborhoods" className="btn btn-primary">
          View All Neighborhoods
        </Link>
      </div>
    );
  }

  // SEO data for this specific neighborhood
  const neighborhoodSchema = {
    "@context": "https://schema.org",
    "@type": "Residence",
    "name": neighborhood.name,
    "description": neighborhood.longDescription,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": neighborhood.location.split(', ')[0],
      "addressRegion": "GA",
      "addressCountry": "US"
    },
    "amenityFeature": neighborhood.amenities.map(amenity => ({
      "@type": "LocationFeatureSpecification",
      "name": amenity
    })),
    "builder": {
      "@type": "Organization",
      "name": "Yeremian Lifestyle Homes"
    }
  };

  return (
    <div>
      <SEO
        title={`${neighborhood.name} - ${neighborhood.location} | Yeremian Lifestyle Homes Community`}
        description={`${neighborhood.name} in ${neighborhood.location} - ${neighborhood.longDescription.substring(0, 140)}... ${neighborhood.priceRange}`}
        keywords={`${neighborhood.name}, ${neighborhood.location}, Yeremian Lifestyle Homes community, new homes ${neighborhood.location}, ${neighborhood.homes.join(', ')} home plans`}
        url={`https://yeremianlifestylehomes.com/neighborhoods/${neighborhood.id}`}
        type="place"
        schemaData={neighborhoodSchema}
      />
      {/* Hero Section */}
      <div className="relative h-80 md:h-96 lg:h-[500px] overflow-hidden">
        <img 
          src={neighborhood.image} 
          alt={neighborhood.name} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 text-white">
          <div className="container-custom">
            <h1 className="drop-shadow-sm mb-2">{neighborhood.name}</h1>
            <p className="text-xl text-white/90 drop-shadow-sm">{neighborhood.location}</p>
          </div>
        </div>
      </div>

      <div className="container-custom py-12 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-serif mb-6">About {neighborhood.name}</h2>
            <p className="text-lg text-gray-700 mb-8">{neighborhood.longDescription}</p>
            
            <h3 className="text-xl font-serif mb-4">Community Amenities</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
              {neighborhood.amenities.map((amenity, index) => (
                <li key={index} className="flex items-center">
                  <svg className="h-5 w-5 text-primary mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {amenity}
                </li>
              ))}
            </ul>
            
            <h3 className="text-xl font-serif mb-4">Available Home Plans</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {neighborhood.homes.map((home, index) => (
                <Link 
                  key={index} 
                  to={`/homes/${home.toLowerCase()}`}
                  className="bg-gray-50 hover:bg-primary/5 p-4 rounded text-center transition"
                >
                  <span className="text-dark">{home}</span>
                </Link>
              ))}
            </div>

            {/* Coming Soon Section - Only for Castleberry Ridge */}
            {id === 'castleberry' && (
              <div className="mb-12">
                <div className="text-center py-12">
                  <div className="bg-primary/5 rounded-lg p-8 max-w-md mx-auto">
                    <h3 className="text-2xl font-serif text-primary mb-4">Coming Soon</h3>
                    <p className="text-gray-700 mb-6">
                      Castleberry Ridge properties will be available soon. Contact us to be notified when homes become available.
                    </p>
                    <Link to="/contact" className="btn btn-primary">
                      Contact Us for Updates
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Lot Master Plan Section - Only for Castleberry Ridge */}
            {id === 'castleberry' && (
              <div className="mb-12">
                <h3 className="text-xl font-serif mb-6">Lot Master Plan</h3>
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <img 
                    src={castleberryMasterPlan} 
                    alt="Castleberry Ridge Lot Master Plan" 
                    className="w-full h-auto object-contain"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Castleberry Ridge Lot Master Plan - Dawsonville, GA
                </p>
              </div>
            )}

            {/* Properties Section - Using the component */}
            {id && <PropertiesSection neighborhoodId={id} />}
          </div>
          
          <div className="lg:col-span-1">
            <div className="bg-primary/5 rounded-lg p-6 shadow-sm sticky top-24">
              <h3 className="text-xl font-serif mb-4">Neighborhood Details</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Location</div>
                  <div className="font-medium">{neighborhood.location}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Price Range</div>
                  <div className="font-medium text-primary">{neighborhood.priceRange}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Available Home Plans</div>
                  <div className="font-medium">{neighborhood.homes.length} Plans</div>
                </div>
              </div>
              
              <div className="mt-8">
                <h4 className="font-medium mb-3">Interested in this community?</h4>
                <Link to="/contact" className="btn btn-primary w-full mb-3">Contact Us</Link>
                <Link to="/homes" className="btn btn-outline w-full">View All Homes</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Neighborhood; 